/**
 * F-508-FE-12: TemplateManagement
 *
 * /admin/templates
 * - Specialization filter
 * - Template list with version status badges
 * - Create template form (modal)
 * - File upload (.docx/.pdf/.txt/.md, 5MB) with processing indicator
 * - Version history panel (expandable): version list, activate DRAFT, superseded display, prompt preview
 *
 * API:
 * - GET /admin/templates?specializationId=
 * - POST /admin/templates
 * - POST /admin/templates/:id/upload (multipart)
 * - POST /admin/templates/:id/versions/:versionId/activate
 * - GET /admin/templates/:id/versions/:versionId/preview
 */

import { useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AdminLayout } from '@/layouts/AdminLayout';
import { api, getAuthToken } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type VersionStatus = 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'ERROR' | 'PROCESSING' | 'PENDING_REVIEW';

interface TemplateVersion {
  id: string;
  versionNumber: number;
  status: VersionStatus;
  createdAt: string;
  supersededByVersion?: number;
  fileName?: string;
  /** F-TemplatesFromRealNotes Task 20: PLACEHOLDER (default) or EXAMPLE (derived from real note). */
  sourceMode?: 'PLACEHOLDER' | 'EXAMPLE';
  /** F-TemplatesFromRealNotes Task 20: PHI counts for EXAMPLE versions. */
  redactionReport?: {
    regexHits: Record<string, number>;
    llmRedactionsApplied: boolean;
  } | null;
}

type OwnerScope = 'PLATFORM' | 'HOSPITAL' | 'DOCTOR';

interface Template {
  id: string;
  name: string;
  description: string | null;
  specializationId: string;
  status: 'ACTIVE' | 'INACTIVE';
  ownerScope: OwnerScope;
  hospitalId: string | null;
  doctorId: string | null;
  updatedAt: string;
  specialization: {
    id: string;
    name: string;
    shortCode: string;
  };
  currentVersion: {
    id: string;
    versionNumber: number;
    status: VersionStatus;
    createdAt: string;
  } | null;
  versions?: TemplateVersion[];
}

interface Specialization {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Version status badge
// ---------------------------------------------------------------------------

function VersionStatusBadge({ status }: { status: VersionStatus }) {
  const map: Record<VersionStatus, string> = {
    DRAFT: 'bg-amber-50 text-amber-700 border-amber-200',
    ACTIVE: 'bg-teal-50 text-teal-700 border-teal-200',
    SUPERSEDED: 'bg-gray-100 text-gray-500 border-gray-200',
    ERROR: 'bg-red-50 text-red-700 border-red-200',
    PROCESSING: 'bg-blue-50 text-blue-700 border-blue-200',
    PENDING_REVIEW: 'bg-amber-50 text-amber-700 border-amber-300',
  };
  const labels: Record<VersionStatus, string> = {
    DRAFT: 'Draft',
    ACTIVE: 'Active',
    SUPERSEDED: 'Superseded',
    ERROR: 'Error',
    PROCESSING: 'Processing…',
    PENDING_REVIEW: 'Pending Review',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${map[status]}`}>
      {status === 'PROCESSING' && (
        <span className="w-2 h-2 rounded-full border border-blue-600 border-t-transparent animate-spin me-1.5" />
      )}
      {labels[status]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// File upload helper
// ---------------------------------------------------------------------------

const ACCEPTED_TYPES = ['.docx', '.pdf', '.txt', '.md'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function validateFile(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!['docx', 'pdf', 'txt', 'md'].includes(ext)) {
    return 'Only .docx, .pdf, .txt, or .md files are supported.';
  }
  if (file.size > MAX_FILE_SIZE) {
    return 'File must be under 5MB.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Prompt Preview Modal
// ---------------------------------------------------------------------------

interface PreviewModalProps {
  open: boolean;
  templateId: string;
  versionId: string;
  versionNumber: number;
  onClose: () => void;
}

function PreviewModal({ open, templateId, versionId, versionNumber, onClose }: PreviewModalProps) {
  const { data, isLoading } = useQuery<{ promptText: string }>({
    queryKey: ['admin', 'templates', templateId, 'versions', versionId, 'preview'],
    queryFn: async () => {
      const res = await api.get<{ data: { promptText: string } }>(`/admin/templates/${templateId}/versions/${versionId}/preview`);
      return res.data;
    },
    enabled: open,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-clinical-border flex-none">
          <h3 className="font-heading text-base font-semibold text-clinical-charcoal">
            Prompt Preview — v{versionNumber}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-4 w-full rounded" />
              ))}
            </div>
          ) : (
            <pre className="text-xs font-mono text-clinical-charcoal whitespace-pre-wrap bg-clinical-cream rounded-clinical p-4 border border-clinical-border">
              {data?.promptText ?? 'No prompt available.'}
            </pre>
          )}
        </div>
        <div className="px-6 py-4 border-t border-clinical-border flex-none">
          <p className="text-xs font-body text-clinical-text-muted">
            Read-only view of the generated LLM prompt structure for this template version.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Watchlist Preview Modal
// ---------------------------------------------------------------------------

interface WatchlistPreviewModalProps {
  open: boolean;
  templateId: string;
  versionId: string;
  versionNumber: number;
  onClose: () => void;
}

function WatchlistPreviewModal({ open, templateId, versionId, versionNumber, onClose }: WatchlistPreviewModalProps) {
  const { data, isLoading } = useQuery<{ content: unknown }>({
    queryKey: ['admin', 'templates', templateId, 'watchlist', versionId, 'preview'],
    queryFn: () => api.get<{ content: unknown }>(`/admin/templates/${templateId}/watchlist/${versionId}/preview`),
    enabled: open,
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-clinical-border flex-none">
          <h3 className="font-heading text-base font-semibold text-clinical-charcoal">
            Watchlist Preview — v{versionNumber}
          </h3>
          <button type="button" onClick={onClose} className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="skeleton h-4 w-full rounded" />
              ))}
            </div>
          ) : (
            <pre className="text-xs font-mono text-clinical-charcoal whitespace-pre-wrap bg-clinical-cream rounded-clinical p-4 border border-clinical-border">
              {JSON.stringify(data?.content ?? data, null, 2)}
            </pre>
          )}
        </div>
        <div className="px-6 py-4 border-t border-clinical-border flex-none">
          <p className="text-xs font-body text-clinical-text-muted">
            Read-only view of the keyword watchlist JSON for this version.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scope badge
// ---------------------------------------------------------------------------

function ScopeBadge({ scope }: { scope: OwnerScope }) {
  const map: Record<OwnerScope, string> = {
    PLATFORM: 'bg-purple-50 text-purple-700 border-purple-200',
    HOSPITAL: 'bg-blue-50 text-blue-700 border-blue-200',
    DOCTOR: 'bg-teal-50 text-teal-700 border-teal-200',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${map[scope]}`}>
      {scope.charAt(0) + scope.slice(1).toLowerCase()}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Change scope modal (Task 21)
// ---------------------------------------------------------------------------

interface ChangeScopeModalProps {
  open: boolean;
  template: Template;
  specializations: Specialization[];
  onClose: () => void;
  onSuccess: () => void;
}

function ChangeScopeModal({ open, template, onClose, onSuccess, specializations: _specializations }: ChangeScopeModalProps) {
  const [targetScope, setTargetScope] = useState<OwnerScope>(template.ownerScope);
  const [hospitalId, setHospitalId] = useState(template.hospitalId ?? '');
  const [doctorId, setDoctorId] = useState(template.doctorId ?? '');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when template changes
  const handleOpen = () => {
    setTargetScope(template.ownerScope);
    setHospitalId(template.hospitalId ?? '');
    setDoctorId(template.doctorId ?? '');
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (targetScope === 'HOSPITAL' && !hospitalId.trim()) {
      setError('Hospital ID is required when scope is HOSPITAL.');
      return;
    }
    if (targetScope === 'DOCTOR' && (!hospitalId.trim() || !doctorId.trim())) {
      setError('Hospital ID and Doctor ID are required when scope is DOCTOR.');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.patch(`/admin/templates/${template.id}/scope`, {
        targetScope,
        ...(targetScope !== 'PLATFORM' ? { hospitalId: hospitalId.trim() } : {}),
        ...(targetScope === 'DOCTOR' ? { doctorId: doctorId.trim() } : {}),
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change scope.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-base font-semibold text-clinical-charcoal">
            Change Template Scope
          </h3>
          <button type="button" onClick={onClose} className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="px-6 py-5 space-y-4">
          <p className="text-sm font-body text-clinical-text-muted">
            Current scope: <ScopeBadge scope={template.ownerScope} />
          </p>
          <div>
            <label className="block text-xs font-body font-semibold text-clinical-charcoal mb-1">Target Scope</label>
            <div className="flex gap-3">
              {(['PLATFORM', 'HOSPITAL', 'DOCTOR'] as OwnerScope[]).map((s) => (
                <label key={s} className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="targetScope"
                    value={s}
                    checked={targetScope === s}
                    onChange={() => { setTargetScope(s); }}
                    className="accent-clinical-teal"
                  />
                  <span className="text-sm font-body text-clinical-charcoal capitalize">{s.toLowerCase()}</span>
                </label>
              ))}
            </div>
          </div>
          {(targetScope === 'HOSPITAL' || targetScope === 'DOCTOR') && (
            <div>
              <label className="block text-xs font-body font-semibold text-clinical-charcoal mb-1">Hospital ID</label>
              <input
                type="text"
                value={hospitalId}
                onChange={(e) => setHospitalId(e.target.value)}
                className="form-input"
                placeholder="UUID of the hospital"
              />
            </div>
          )}
          {targetScope === 'DOCTOR' && (
            <div>
              <label className="block text-xs font-body font-semibold text-clinical-charcoal mb-1">Doctor ID</label>
              <input
                type="text"
                value={doctorId}
                onChange={(e) => setDoctorId(e.target.value)}
                className="form-input"
                placeholder="UUID of the doctor"
              />
            </div>
          )}
          {error && (
            <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm font-body text-red-700">{error}</p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-sm min-h-[36px]">Cancel</button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary px-4 py-2 text-sm min-h-[36px]"
            >
              {isSubmitting ? 'Saving…' : 'Change Scope'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Version history panel
// ---------------------------------------------------------------------------

interface VersionHistoryPanelProps {
  template: Template;
  onActivated: () => void;
}

// Collapsible redaction report for EXAMPLE-mode versions (Task 20)
function RedactionReportSection({ report }: {
  report: NonNullable<TemplateVersion['redactionReport']>;
}) {
  const [open, setOpen] = useState(false);
  const totalHits = Object.values(report.regexHits).reduce((s, n) => s + n, 0);

  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-clinical-text-muted hover:text-clinical-charcoal transition-colors"
      >
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        Redaction report
        {totalHits > 0 && (
          <span className="text-clinical-text-muted">({totalHits} identifier{totalHits !== 1 ? 's' : ''} removed)</span>
        )}
      </button>
      {open && (
        <div className="mt-1 ml-4 rounded-clinical border border-clinical-border bg-white px-3 py-2 text-xs font-body text-clinical-text-muted space-y-0.5">
          {Object.entries(report.regexHits).length > 0 ? (
            Object.entries(report.regexHits).map(([type, count]) => (
              <div key={type} className="flex gap-2">
                <span className="font-semibold text-clinical-charcoal w-28 shrink-0">{type}</span>
                <span>{count} occurrence{count !== 1 ? 's' : ''} redacted</span>
              </div>
            ))
          ) : (
            <div>No regex identifiers found.</div>
          )}
          <div className="pt-1 border-t border-clinical-border mt-1">
            Name / location redaction: {report.llmRedactionsApplied ? 'applied' : 'not applied'}
          </div>
        </div>
      )}
    </div>
  );
}

function VersionHistoryPanel({ template, onActivated }: VersionHistoryPanelProps) {
  const queryClient = useQueryClient();
  const [previewVersion, setPreviewVersion] = useState<TemplateVersion | null>(null);

  const { data: versions, isLoading } = useQuery<TemplateVersion[]>({
    queryKey: ['admin', 'templates', template.id, 'versions'],
    queryFn: async () => {
      const res = await api.get<{ data: TemplateVersion[] }>(`/admin/templates/${template.id}/versions`);
      return res.data;
    },
  });

  const activateMutation = useMutation({
    mutationFn: ({ templateId, versionId }: { templateId: string; versionId: string }) =>
      api.post(`/admin/templates/${templateId}/versions/${versionId}/activate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'templates', template.id, 'versions'] });
      onActivated();
    },
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (isLoading) {
    return (
      <div className="mt-4 space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="skeleton h-8 w-full rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-clinical-border pt-4">
      <h5 className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide mb-3">
        Version History
      </h5>
      {(versions?.length ?? 0) === 0 ? (
        <p className="text-xs font-body text-clinical-text-muted">No versions yet. Upload a file to create a version.</p>
      ) : (
        <div className="space-y-2">
          {versions?.map((v) => (
            <div
              key={v.id}
              className="py-2 px-3 bg-clinical-cream rounded-clinical"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-xs font-semibold text-clinical-charcoal">v{v.versionNumber}</span>
                  <VersionStatusBadge status={v.status} />
                  {/* F-TemplatesFromRealNotes Task 20: example-derived badge */}
                  {v.sourceMode === 'EXAMPLE' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-purple-50 text-purple-700 border-purple-200">
                      Example-derived
                    </span>
                  )}
                  {v.supersededByVersion && (
                    <span className="text-xs font-body text-clinical-text-muted">
                      Superseded by v{v.supersededByVersion}
                    </span>
                  )}
                  <span className="text-xs font-body text-clinical-text-muted">{formatDate(v.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setPreviewVersion(v)}
                    className="text-xs text-clinical-text-muted font-medium hover:text-clinical-charcoal"
                  >
                    Preview
                  </button>
                  {v.status === 'DRAFT' && (
                    <>
                      <span className="text-clinical-border">|</span>
                      <button
                        type="button"
                        onClick={() => activateMutation.mutate({ templateId: template.id, versionId: v.id })}
                        disabled={activateMutation.isPending}
                        className="text-xs text-clinical-teal font-medium hover:underline"
                      >
                        Activate
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* F-TemplatesFromRealNotes Task 20: collapsible redaction report */}
              {v.sourceMode === 'EXAMPLE' && v.redactionReport && (
                <RedactionReportSection report={v.redactionReport} />
              )}
            </div>
          ))}
        </div>
      )}

      {previewVersion && (
        <PreviewModal
          open
          templateId={template.id}
          versionId={previewVersion.id}
          versionNumber={previewVersion.versionNumber}
          onClose={() => setPreviewVersion(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Watchlist version history panel
// ---------------------------------------------------------------------------

interface WatchlistVersion {
  id: string;
  versionNumber: number;
  status: VersionStatus;
  createdAt: string;
}

interface WatchlistVersionHistoryPanelProps {
  template: Template;
  onActivated: () => void;
}

function WatchlistVersionHistoryPanel({ template, onActivated }: WatchlistVersionHistoryPanelProps) {
  const queryClient = useQueryClient();
  const [previewVersion, setPreviewVersion] = useState<WatchlistVersion | null>(null);

  const { data: versions, isLoading } = useQuery<WatchlistVersion[]>({
    queryKey: ['admin', 'templates', template.id, 'watchlist'],
    queryFn: async () => {
      const res = await api.get<{ data: WatchlistVersion[] }>(`/admin/templates/${template.id}/watchlist`);
      return res.data;
    },
  });

  const activateMutation = useMutation({
    mutationFn: ({ templateId, versionId }: { templateId: string; versionId: string }) =>
      api.post(`/admin/templates/${templateId}/watchlist/${versionId}/activate`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'templates', template.id, 'watchlist'] });
      onActivated();
    },
  });

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  if (isLoading) {
    return (
      <div className="mt-4 space-y-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="skeleton h-8 w-full rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4 border-t border-clinical-border pt-4">
      <h5 className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide mb-3">
        Keyword Watchlist Versions
      </h5>
      {(versions?.length ?? 0) === 0 ? (
        <p className="text-xs font-body text-clinical-text-muted">No watchlist versions yet. Upload a JSON file to create one.</p>
      ) : (
        <div className="space-y-2">
          {versions?.map((v) => (
            <div key={v.id} className="flex items-center justify-between py-2 px-3 bg-clinical-cream rounded-clinical">
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs font-semibold text-clinical-charcoal">v{v.versionNumber}</span>
                <VersionStatusBadge status={v.status} />
                <span className="text-xs font-body text-clinical-text-muted">{formatDate(v.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setPreviewVersion(v)} className="text-xs text-clinical-text-muted font-medium hover:text-clinical-charcoal">
                  Preview
                </button>
                {v.status === 'DRAFT' && (
                  <>
                    <span className="text-clinical-border">|</span>
                    <button
                      type="button"
                      onClick={() => activateMutation.mutate({ templateId: template.id, versionId: v.id })}
                      disabled={activateMutation.isPending}
                      className="text-xs text-clinical-teal font-medium hover:underline"
                    >
                      Activate
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {previewVersion && (
        <WatchlistPreviewModal
          open
          templateId={template.id}
          versionId={previewVersion.id}
          versionNumber={previewVersion.versionNumber}
          onClose={() => setPreviewVersion(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload zone
// ---------------------------------------------------------------------------

interface UploadZoneProps {
  templateId: string;
  onUploaded: () => void;
}

function UploadZone({ templateId, onUploaded }: UploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const upload = useCallback(async (file: File) => {
    const err = validateFile(file);
    if (err) {
      setFileError(err);
      return;
    }

    setFileError('');
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
      const token = await getAuthToken();
      // We can't use api.post here because it's JSON-based; use fetch directly for multipart
      const res = await fetch(`${BASE}/admin/templates/${templateId}/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });
      if (!res.ok) {
        const body = await res.json() as { message?: string };
        throw new Error(body.message ?? `Upload failed (${res.status})`);
      }
      onUploaded();
    } catch (err: unknown) {
      setFileError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [templateId, onUploaded]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void upload(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void upload(file);
  };

  return (
    <div className="mt-3">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-clinical p-4 text-center cursor-pointer transition-colors ${
          isUploading
            ? 'border-clinical-teal bg-teal-50 cursor-wait'
            : 'border-clinical-border hover:border-clinical-teal hover:bg-clinical-cream/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-5 h-5 border-2 border-clinical-teal border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-body text-clinical-teal">Uploading and processing…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <svg className="w-5 h-5 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-xs font-body text-clinical-text-muted">
              Drop a file or <span className="text-clinical-teal font-medium">browse</span>
            </p>
            <p className="text-xs font-body text-clinical-text-muted">.docx, .pdf, .txt, .md — max 5MB</p>
          </div>
        )}
      </div>
      {fileError && (
        <p className="text-xs font-body text-red-600 mt-1">{fileError}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Watchlist upload zone
// ---------------------------------------------------------------------------

interface WatchlistUploadZoneProps {
  templateId: string;
  onUploaded: () => void;
}

function WatchlistUploadZone({ templateId, onUploaded }: WatchlistUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const upload = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (ext !== 'json') {
      setFileError('Only .json files are supported.');
      return;
    }
    if (file.size > 1 * 1024 * 1024) {
      setFileError('File must be under 1MB.');
      return;
    }

    setFileError('');
    setWarnings([]);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
      const token = await getAuthToken();
      const res = await fetch(`${BASE}/admin/templates/${templateId}/watchlist/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      });
      const body = await res.json() as { message?: string; data?: { warnings?: string[] } };
      if (!res.ok) {
        throw new Error(body.message ?? `Upload failed (${res.status})`);
      }
      if (body.data?.warnings && body.data.warnings.length > 0) {
        setWarnings(body.data.warnings);
      }
      onUploaded();
    } catch (err: unknown) {
      setFileError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [templateId, onUploaded]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void upload(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void upload(file);
  };

  return (
    <div className="mt-3">
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-clinical p-4 text-center cursor-pointer transition-colors ${
          isUploading
            ? 'border-clinical-teal bg-teal-50 cursor-wait'
            : 'border-clinical-border hover:border-clinical-teal hover:bg-clinical-cream/50'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="hidden"
          disabled={isUploading}
        />
        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-5 h-5 border-2 border-clinical-teal border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-body text-clinical-teal">Uploading watchlist…</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1">
            <svg className="w-5 h-5 text-clinical-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            <p className="text-xs font-body text-clinical-text-muted">
              Drop a watchlist or <span className="text-clinical-teal font-medium">browse</span>
            </p>
            <p className="text-xs font-body text-clinical-text-muted">.json only — max 1MB</p>
          </div>
        )}
      </div>
      {fileError && (
        <p className="text-xs font-body text-red-600 mt-1">{fileError}</p>
      )}
      {warnings.length > 0 && (
        <div className="mt-2 rounded-clinical border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs font-body font-semibold text-amber-700 mb-1">Warnings:</p>
          <ul className="text-xs font-body text-amber-600 list-disc list-inside">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create template modal
// ---------------------------------------------------------------------------

interface CreateModalProps {
  open: boolean;
  specializations: Specialization[];
  defaultSpecId: string;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateModal({ open, specializations, defaultSpecId, onClose, onSuccess }: CreateModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [specializationId, setSpecializationId] = useState(defaultSpecId || specializations[0]?.id || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !specializationId) return;
    setIsSubmitting(true);
    setError('');
    try {
      await api.post('/admin/templates', {
        name: name.trim(),
        description: description.trim(),
        specializationId,
      });
      onSuccess();
      onClose();
      setName('');
      setDescription('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create template.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-clinical-charcoal/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-clinical-lg max-w-md w-full p-6">
        <h3 className="font-heading text-lg font-semibold text-clinical-charcoal mb-5">
          Create Template
        </h3>
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
          <div>
            <label htmlFor="tmpl-name" className="form-label">Template Name</label>
            <input
              id="tmpl-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="form-input"
              placeholder="e.g. OB/GYN Consultation Note"
              required
              autoFocus
            />
          </div>
          <div>
            <label htmlFor="tmpl-spec" className="form-label">Specialization</label>
            <select
              id="tmpl-spec"
              value={specializationId}
              onChange={(e) => setSpecializationId(e.target.value)}
              className="form-input"
              required
            >
              <option value="">Select specialization…</option>
              {specializations.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <p className="text-xs font-body text-clinical-text-muted mt-1">
              Specialization binding is permanent after first activation.
            </p>
          </div>
          <div>
            <label htmlFor="tmpl-desc" className="form-label">Description</label>
            <textarea
              id="tmpl-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="form-textarea"
              placeholder="Brief description…"
              rows={3}
            />
          </div>
          {error && (
            <div className="rounded-clinical border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm font-body text-red-700">{error}</p>
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-sm min-h-[36px]">Cancel</button>
            <button type="submit" disabled={isSubmitting || !name.trim() || !specializationId} className="btn-primary px-4 py-2 text-sm min-h-[36px]">
              {isSubmitting ? 'Creating…' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template row (expandable)
// ---------------------------------------------------------------------------

interface TemplateRowProps {
  template: Template;
  specializations: Specialization[];
  onRefetch: () => void;
}

function TemplateRow({ template, specializations, onRefetch }: TemplateRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [showScopeModal, setShowScopeModal] = useState(false);

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <>
      <tr className="hover:bg-clinical-cream/50 transition-colors">
        <td className="px-5 py-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-2 font-medium text-clinical-charcoal hover:text-clinical-teal transition-colors text-start"
          >
            <svg
              className={`w-4 h-4 flex-none transition-transform duration-150 ${expanded ? 'rotate-90' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
            {template.name}
          </button>
        </td>
        <td className="px-4 py-3 text-clinical-text-muted text-sm">{template.specialization?.name}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <ScopeBadge scope={template.ownerScope ?? 'PLATFORM'} />
            <button
              type="button"
              onClick={() => setShowScopeModal(true)}
              className="text-xs font-body text-clinical-teal hover:underline"
              aria-label={`Change scope of ${template.name}`}
            >
              Change
            </button>
          </div>
        </td>
        <td className="px-4 py-3">
          {template.currentVersion?.versionNumber ? (
            <span className="font-mono text-xs text-clinical-charcoal">v{template.currentVersion.versionNumber}</span>
          ) : (
            <span className="text-xs text-clinical-text-muted">—</span>
          )}
        </td>
        <td className="px-4 py-3">
          {template.currentVersion?.status ? (
            <VersionStatusBadge status={template.currentVersion.status} />
          ) : (
            <span className="text-xs text-clinical-text-muted">No versions</span>
          )}
        </td>
        <td className="px-5 py-3 text-clinical-text-muted text-xs">{formatDate(template.updatedAt)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-5 pb-4 bg-clinical-cream/50">
            <div className="pt-2">
              {template.description && (
                <p className="text-xs font-body text-clinical-text-muted mb-3">{template.description}</p>
              )}
              <div>
                <p className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide mb-2">
                  Upload New Version
                </p>
                <UploadZone
                  templateId={template.id}
                  onUploaded={() => {
                    onRefetch();
                  }}
                />
              </div>
              <VersionHistoryPanel template={template} onActivated={onRefetch} />
              {/* Watchlist section */}
              <div className="mt-6">
                <p className="text-xs font-body font-semibold text-clinical-text-muted uppercase tracking-wide mb-2">
                  Upload Keyword Watchlist
                </p>
                <WatchlistUploadZone
                  templateId={template.id}
                  onUploaded={() => {
                    onRefetch();
                  }}
                />
              </div>
              <WatchlistVersionHistoryPanel template={template} onActivated={onRefetch} />
            </div>
          </td>
        </tr>
      )}
      {showScopeModal && (
        <ChangeScopeModal
          open={showScopeModal}
          template={template}
          specializations={specializations}
          onClose={() => setShowScopeModal(false)}
          onSuccess={onRefetch}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function TemplateManagement() {
  const queryClient = useQueryClient();
  const [specFilter, setSpecFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: specializations } = useQuery<Specialization[]>({
    queryKey: ['admin', 'specializations', 'active'],
    queryFn: async () => {
      const res = await api.get<{ data: Specialization[] }>('/admin/specializations?status=ACTIVE');
      return res.data;
    },
  });

  const params = new URLSearchParams(specFilter ? { specializationId: specFilter } : {});

  const { data: templates, isLoading, refetch } = useQuery<Template[]>({
    queryKey: ['admin', 'templates', specFilter],
    queryFn: async () => {
      const res = await api.get<{ data: Template[] }>(`/admin/templates?${params.toString()}`);
      return res.data;
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });

  return (
    <AdminLayout breadcrumbs={[{ label: 'Templates' }]}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-clinical-charcoal">Templates</h1>
          <p className="text-sm font-body text-clinical-text-muted mt-1">
            Manage note generation templates for each specialization.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="btn-primary text-sm px-4 py-2 min-h-[40px]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          New Template
        </button>
      </div>

      {/* Specialization filter */}
      <div className="mb-4">
        <select
          value={specFilter}
          onChange={(e) => setSpecFilter(e.target.value)}
          className="form-input w-auto text-sm py-2 min-h-[36px]"
          aria-label="Filter by specialization"
        >
          <option value="">All specializations</option>
          {specializations?.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="clinical-card overflow-hidden">
        {isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="skeleton h-12 w-full rounded" />
            ))}
          </div>
        ) : (templates?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm font-body text-clinical-text-muted">No templates found.</p>
            <button
              type="button"
              onClick={() => setShowCreateModal(true)}
              className="btn-primary text-sm px-4 py-2 min-h-[40px] mt-4"
            >
              Create First Template
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="border-b border-clinical-border bg-clinical-cream">
                  <th className="text-start px-5 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Name</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Specialization</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Scope</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Version</th>
                  <th className="text-start px-4 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Status</th>
                  <th className="text-start px-5 py-3 text-xs font-semibold text-clinical-text-muted uppercase tracking-wide">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-clinical-border">
                {templates?.map((template) => (
                  <TemplateRow
                    key={template.id}
                    template={template}
                    specializations={specializations ?? []}
                    onRefetch={() => {
                      void refetch();
                      invalidate();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateModal
        open={showCreateModal}
        specializations={specializations ?? []}
        defaultSpecId={specFilter}
        onClose={() => setShowCreateModal(false)}
        onSuccess={invalidate}
      />
    </AdminLayout>
  );
}
